import React, { useEffect, useRef } from 'react';

type Props = {
  innerHTML: string;
};

export const HTMLElement = ({ innerHTML }: Props): React.ReactNode => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    const parentElement = ref.current;

    if (parentElement === null || innerHTML === undefined) {
      return;
    }

    // Clear the wrapper before adding new content
    while (parentElement.firstChild) {
      parentElement.removeChild(parentElement.firstChild);
    }

    // https://dev.to/christo_pr/render-dangerous-content-with-react-2j7j
    const fragment = document.createRange().createContextualFragment(innerHTML);

    const scripts: HTMLScriptElement[] = [];

    // Extract and remove script tags from the fragment
    fragment.querySelectorAll('script').forEach((script) => {
      scripts.push(script);
      script.parentNode?.removeChild(script);
    });

    // Append the fragment to the wrapper
    parentElement.appendChild(fragment);

    // Load the scripts in order
    (async function () {
      for (const script of scripts) {
        if (cancelled) {
          return;
        }

        const isAsync = script.hasAttribute('async');
        const isDefer = script.hasAttribute('defer');

        if (isAsync || isDefer) {
          // If the script is async or defer, we don't need to wait for it to load
          loadScript(script, parentElement);
          continue;
        }

        try {
          await loadScript(script, parentElement);
        } catch (error) {
          console.error(error);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [innerHTML]);

  return <div ref={ref} />;
};

const loadScript = (scriptElement: HTMLScriptElement, parentElement: HTMLElement): Promise<void> => {
  return new Promise((resolve, reject) => {
    const newScript = document.createElement('script');

    for (const attr of scriptElement.attributes) {
      newScript.setAttribute(attr.name, attr.value);
    }

    if (scriptElement.src) {
      // External script
      newScript.onload = () => resolve();
      newScript.onerror = () => reject(new Error(`Failed to load script: ${scriptElement.src}`));
      newScript.src = scriptElement.src;
    } else {
      // Inline script
      newScript.textContent = scriptElement.textContent;
    }

    parentElement.appendChild(newScript);

    // Resolve immediately for scripts without a src attribute
    if (scriptElement.hasAttribute('src') === false) {
      resolve();
    }
  });
};
